import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SubscriptionData, TokenUsageData } from '../services/stripe.services';

interface SubscriptionStore {
    subscription: SubscriptionData | null;
    tokenUsage: TokenUsageData | null;
    lastFetched: number | null;

    setSubscription: (data: SubscriptionData) => void;
    setTokenUsage: (data: TokenUsageData) => void;
    clearSubscriptionData: () => void;

    isPlusUser: () => boolean;
    isServiceUser: () => boolean;
    tokenUsagePercentage: () => number;
    isApproachingLimit: () => boolean;
    hasExceededTokenLimit: () => boolean;
    canPerformTokenOperation: () => boolean;
    updateTokenStatusFromError: (error: any) => void;
    
    hasFeature: (featureName: string) => boolean;
    getEnabledFeatures: () => string[];
}

export const useSubscriptionStore = create<SubscriptionStore>()(
    persist(
        (set, get) => ({
            subscription: null,
            tokenUsage: null,
            lastFetched: null,

            setSubscription: (data: SubscriptionData) => {
                set({
                    subscription: data,
                    lastFetched: Date.now()
                });
            },

            setTokenUsage: (data: TokenUsageData) => {
                set({
                    tokenUsage: data,
                    lastFetched: Date.now()
                });
            },

            clearSubscriptionData: () => {
                set({
                    subscription: null,
                    tokenUsage: null,
                    lastFetched: null
                });
                // Also remove the persisted localStorage key so the
                // persist middleware doesn't re-hydrate stale plan data
                // after a user switch (e.g. Premium → Free login).
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('kode-sherpa-subscription');
                }
            },

            isPlusUser: () => {
                const { subscription } = get();
                return subscription?.planId === 'PLUS' && subscription?.hasSubscription === true;
            },

            isServiceUser: () => {
                const { subscription } = get();
                return subscription?.planId === 'SERVICE';
            },

            tokenUsagePercentage: () => {
                const { tokenUsage } = get();
                if (!tokenUsage || tokenUsage.limit === 0 || tokenUsage.limit === Infinity) return 0;
                return Math.min((tokenUsage.used / tokenUsage.limit) * 100, 100);
            },

            isApproachingLimit: () => {
                const percentage = get().tokenUsagePercentage();
                return percentage >= 80;
            },

            hasExceededTokenLimit: () => {
                const { tokenUsage } = get();
                if (!tokenUsage) return false;
                // Service users with unlimited tokens never exceed limit
                if (tokenUsage.limit === Infinity || tokenUsage.remaining === Infinity) return false;
                return tokenUsage.remaining <= 0;
            },

            canPerformTokenOperation: () => {
                // Service users can always perform operations
                if (get().isServiceUser()) return true;
                return !get().hasExceededTokenLimit();
            },

            updateTokenStatusFromError: (error: any) => {
                if (error?.tokenStatus) {
                    const tokenStatus = error.tokenStatus;
                    set({
                        tokenUsage: {
                            used: tokenStatus.used || 0,
                            limit: tokenStatus.limit || 0,
                            remaining: tokenStatus.remaining || 0,
                            resetDate: tokenStatus.resetDate || get().tokenUsage?.resetDate || ''
                        },
                        lastFetched: Date.now()
                    });
                }
            },

            hasFeature: (featureName: string) => {
                const { subscription } = get();
                if (!subscription?.features) return false;
                return subscription.features.includes(featureName);
            },

            getEnabledFeatures: () => {
                const { subscription } = get();
                return subscription?.features || [];
            }
        }),
        {
            name: 'kode-sherpa-subscription',
            partialize: (state) => ({
                subscription: state.subscription,
                tokenUsage: state.tokenUsage,
                lastFetched: state.lastFetched
            })
        }
    )
);

/**
 * Select subscription data
 */
export const selectSubscription = (state: SubscriptionStore) => state.subscription;

/**
 * Select token usage data
 */
export const selectTokenUsage = (state: SubscriptionStore) => state.tokenUsage;

/**
 * Select if user is Plus subscriber
 */
export const selectIsPlusUser = (state: SubscriptionStore) => state.isPlusUser();

/**
 * Select token usage percentage
 */
export const selectTokenPercentage = (state: SubscriptionStore) => state.tokenUsagePercentage();

/**
 * Select if approaching limit
 */
export const selectIsApproachingLimit = (state: SubscriptionStore) => state.isApproachingLimit();

